import { Children, createContext, type HTMLAttributes, type ReactNode } from 'react';
import { Button } from '@monksflow/monks-ui';
import { ArrowLeft, type LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

/**
 * Visual variant shared across the composition. Today only `default`
 * (large title + roomy padding) and `compact` (smaller title + tighter
 * padding) exist; new variants are added here so every slot can react.
 */
type SectionPageHeaderVariant = 'default' | 'compact';

const SectionPageHeaderContext = createContext<SectionPageHeaderVariant>('default');

// ─── Container ────────────────────────────────────────────────────────────

interface SectionPageHeaderProps extends HTMLAttributes<HTMLElement> {
  children: ReactNode;
  /** Compact uses smaller title (text-lg) and tighter padding (p-4). */
  variant?: SectionPageHeaderVariant;
}

/**
 * Shared page header container for a page or one of its sections.
 *
 * Owns the OUTER structure (border / radius / bg-card / horizontal
 * layout / variant-driven padding) and propagates the active `variant`
 * to every slot via context so the title, padding, and any future
 * variant-aware children stay in lockstep.
 *
 * Composition pattern (named exports — same convention as
 * `Card`/`CardHeader`/`CardContent` in monks-ui and as
 * `ListPageLayout`/`ListPageBody` in this codebase):
 *
 *   <SectionPageHeader variant="compact">
 *     <SectionPageHeaderIcon icon={FileText} />
 *     <SectionPageHeaderContent>
 *       <SectionPageHeaderTitle>Brief templates</SectionPageHeaderTitle>
 *       <SectionPageHeaderDescription>...</SectionPageHeaderDescription>
 *     </SectionPageHeaderContent>
 *     <SectionPageHeaderActions>
 *       <Button>...</Button>
 *     </SectionPageHeaderActions>
 *   </SectionPageHeader>
 *
 * For pages that lead with a back action instead of a static icon
 * (e.g., "Create project" flow), use `SectionPageHeaderBackButton`:
 *
 *   <SectionPageHeader>
 *     <SectionPageHeaderBackButton onClick={onBack} aria-label="Back to projects" />
 *     <SectionPageHeaderContent>
 *       <SectionPageHeaderTitle>Create project</SectionPageHeaderTitle>
 *       <SectionPageHeaderDescription>...</SectionPageHeaderDescription>
 *     </SectionPageHeaderContent>
 *   </SectionPageHeader>
 *
 * Callers cannot override the icon size, the title typography, the
 * badge styling, or the description tone — every slot enforces the
 * canonical tokens. This is the contract that prevents the
 * "Projects ships size-6, KH ships size-5" drift that the previous
 * configuration-based API allowed.
 */
export function SectionPageHeader({
  children,
  className,
  variant = 'default',
  ...rest
}: SectionPageHeaderProps) {
  const baseClasses = `flex items-center gap-3 rounded-lg bg-card p-4`;
  const composedClassName = className ? `${baseClasses} ${className}` : baseClasses;

  return (
    <SectionPageHeaderContext.Provider value={variant}>
      <header data-testid="section-page-header" className={composedClassName} {...rest}>
        {children}
      </header>
    </SectionPageHeaderContext.Provider>
  );
}

// ─── Icon slot ────────────────────────────────────────────────────────────

type SectionPageHeaderIconProps = {
  /**
   * Replaces the neutral surface classes outright, e.g. with a category tint.
   * Merging would keep the neutral `dark:` background over the tint.
   */
  surfaceClassName?: string;
} & (
  | {
      icon: LucideIcon;
      children?: never;
    }
  | {
      icon?: never;
      children: ReactNode;
    }
);

/**
 * Avatar slot — renders the icon inside the canonical 13.5×13.5
 * surface every one of these headers shares. The slot owns the
 * surface, size, and radius so every page shares the same icon
 * footprint regardless of what fills it.
 *
 * Two modes (discriminated union):
 *
 *   - `icon={LucideIcon}` — pass a lucide component. The slot applies
 *     the standard `size-5 text-foreground` so all lucide icons render
 *     identically across the app. This is the canonical path.
 *
 *   - `<children>` — pass arbitrary content for non-lucide avatars
 *     (e.g. a project's initial letter inside a styled `<span>`).
 *     The avatar wrapper still owns the surface; callers only choose
 *     what fills it.
 */
export function SectionPageHeaderIcon(props: SectionPageHeaderIconProps) {
  return (
    <div
      data-testid="section-page-header-icon"
      className={clsx(
        'flex size-13.5 shrink-0 items-center justify-center rounded',
        props.surfaceClassName ?? 'bg-surface dark:bg-surface-raised text-foreground',
      )}
    >
      {props.icon ? (
        <props.icon data-testid="section-page-header-icon-svg" className="size-5" />
      ) : (
        props.children
      )}
    </div>
  );
}

// ─── Back button slot ─────────────────────────────────────────────────────

interface SectionPageHeaderBackButtonProps {
  /** Click handler for the back button */
  onClick: () => void;
  /** Optional aria-label for accessibility. Defaults to "Go back" */
  'aria-label'?: string;
}

/**
 * Back button slot — renders a ghost icon button with ArrowLeft that
 * replaces the icon slot when navigation is the primary action. Uses the
 * same size-13.5 footprint as the icon slot so the header layout stays
 * identical whether you render an icon or a back button.
 *
 * This is an alternative to `SectionPageHeaderIcon` — use one or the
 * other, not both. When the page header leads with a back action
 * instead of a static icon (e.g. "Create project" flow), use this slot.
 */
export function SectionPageHeaderBackButton({
  onClick,
  'aria-label': ariaLabel,
}: SectionPageHeaderBackButtonProps) {
  const { t } = useTranslation('common');

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={onClick}
      aria-label={ariaLabel ?? t('components.detailHeader.goBack')}
      data-testid="section-page-header-back-button"
      className="size-9.5 shrink-0 p-0 cursor-pointer"
    >
      <ArrowLeft className="size-5" />
    </Button>
  );
}

// ─── Content stack ────────────────────────────────────────────────────────

interface SectionPageHeaderContentProps {
  children: ReactNode;
}

/**
 * Vertical stack that groups the title row (title + optional count)
 * and the description. Renders as `flex-col gap-0.5 flex-1` so it
 * absorbs the available width between the icon and the actions slot.
 *
 * The title row layout (title + count side-by-side) is implemented by
 * the title and count slots themselves — they target this content
 * stack as their visual parent.
 */
export function SectionPageHeaderContent({ children }: SectionPageHeaderContentProps) {
  return (
    <div data-testid="section-page-header-content" className="flex flex-1 flex-col gap-0.5">
      <div className="flex items-center gap-2">{pickTitleRow(children)}</div>
      {pickNonTitleRow(children)}
    </div>
  );
}

/**
 * Internal helper: split content children into the row that pairs with
 * the title (Title only) and the rest (Description, custom slots).
 *
 * The split is structural — the title row gets the `flex items-center
 * gap-2` wrapper. Description and other children render below in document order.
 */
function pickTitleRow(children: ReactNode): ReactNode {
  return filterByDisplayName(children, ['SectionPageHeaderTitle']);
}

function pickNonTitleRow(children: ReactNode): ReactNode {
  return filterByDisplayName(children, ['SectionPageHeaderTitle'], /* exclude */ true);
}

function filterByDisplayName(children: ReactNode, names: string[], exclude = false): ReactNode[] {
  const matches: ReactNode[] = [];
  // Only iterate the top-level children of the content slot — we don't
  // recurse because slot children are flat by convention.
  arrayifyChildren(children).forEach((child) => {
    const displayName = readSlotDisplayName(child);
    if (displayName === null) {
      // Non-element nodes (strings, numbers, fragments) are routed to
      // the non-title row by default so plain-text descriptions still
      // render below the title.
      if (exclude) matches.push(child);
      return;
    }
    const isMatch = names.includes(displayName);
    if (exclude ? !isMatch : isMatch) {
      matches.push(child);
    }
  });
  return matches;
}

function arrayifyChildren(children: ReactNode): ReactNode[] {
  // `Children.toArray` does two important things for us:
  //   1. Strips `false` / `null` / `undefined` nodes — so conditional
  //      slots like `{count !== undefined && <Count />}` can never leak
  //      a falsy value into the non-title row.
  //   2. Auto-assigns stable keys to every kept node — so the arrays
  //      returned by `pickTitleRow` / `pickNonTitleRow` are React-safe
  //      when rendered as a list (no "missing key" warnings).
  return Children.toArray(children);
}

function readSlotDisplayName(node: ReactNode): string | null {
  if (typeof node !== 'object' || node === null || !('type' in node)) {
    return null;
  }
  const { type } = node as { type: unknown };
  if (typeof type === 'function') {
    const fn = type as { displayName?: string; name?: string };
    return fn.displayName ?? fn.name ?? '';
  }
  if (typeof type === 'object' && type !== null && 'displayName' in type) {
    return (type as { displayName?: string }).displayName ?? '';
  }
  return '';
}

// ─── Title slot ───────────────────────────────────────────────────────────

interface SectionPageHeaderTitleProps {
  children: ReactNode;
}

/**
 * Title slot — renders an h1 with the variant-aware typography. The
 * size (`text-2xl` default, `text-lg` compact) is read from the
 * variant context so callers cannot diverge.
 */
export function SectionPageHeaderTitle({ children }: SectionPageHeaderTitleProps) {
  const className = `type-heading-lg text-foreground`;
  return <h1 className={className}>{children}</h1>;
}
SectionPageHeaderTitle.displayName = 'SectionPageHeaderTitle';

// ─── Description slot ────────────────────────────────────────────────────

interface SectionPageHeaderDescriptionProps {
  children: ReactNode;
}

/**
 * Description slot — renders a `<p>` with the canonical secondary tone.
 */
export function SectionPageHeaderDescription({ children }: SectionPageHeaderDescriptionProps) {
  return (
    <p data-testid="section-page-header-description" className="text-sm text-foreground-alt">
      {children}
    </p>
  );
}
SectionPageHeaderDescription.displayName = 'SectionPageHeaderDescription';

// ─── Actions slot ────────────────────────────────────────────────────────

interface SectionPageHeaderActionsProps {
  children: ReactNode;
}

/**
 * Right-aligned actions slot. Caller composes any buttons or controls
 * inside; the slot owns the layout (`flex shrink-0 items-center
 * gap-2`).
 */
export function SectionPageHeaderActions({ children }: SectionPageHeaderActionsProps) {
  return (
    <div data-testid="section-page-header-actions" className="flex shrink-0 items-center gap-2">
      {children}
    </div>
  );
}
SectionPageHeaderActions.displayName = 'SectionPageHeaderActions';
